<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="drivers-page">
    <!-- Page header -->
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">{{ $t('driverPage.title') }}</h1>
      </div>
      <div class="page-header-actions">
        <button
          class="action-pill action-pill--primary"
          @click="dialog = true; action = 'Tworzenie'; currentDriverId = null"
        >
          <v-icon size="14">mdi-account-plus</v-icon>{{ $t('common.add') }}
        </button>
      </div>
    </div>

    <!-- Bolt: spadające oceny i zawieszeni (Fleet API 2.0) -->
    <BoltQualityPanel />

    <!-- Filter section -->
    <div class="filter-section mb-4">
      <div class="filter-section-header">
        <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-filter-variant</v-icon></div>
        <div class="filter-title">Filtry</div>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <div class="search-input">
            <v-icon size="18" color="#6E7691">mdi-magnify</v-icon>
            <input
              v-model="search"
              type="text"
              :placeholder="$t('driverPage.searchLabel')"
            />
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="selectSubCompanyId"
            :items="subCompanies"
            multiple
            item-text="name"
            item-value="id"
            :label="$t('driverPage.labels.department')"
            :rules="[validateOptionalUUID]"
            return-object
            outlined dense hide-details
            prepend-inner-icon="mdi-domain"
          />
        </v-col>
      </v-row>
    </div>

    <v-data-table
      :headers="headers"
      :items="drivers"
      class="elevation-1"
      :loading="loading"
      :search="search"
      :items-per-page="25"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
    >
      <template v-slot:top>
        <div class="table-section-title">
          <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-account-group</v-icon></div>
          <div class="filter-title">{{ $t('driverPage.title') }}</div>
        </div>
      </template>
      <template v-slot:item.details="{ item }">
        <button class="row-btn row-btn--warning" @click="infoDriver(item.id)" :title="$t('common.edit')">
          <v-icon size="14">mdi-pencil</v-icon>
        </button>
        <v-btn
          v-if="isHeadAdmin"
          x-small outlined color="indigo" class="ml-1"
          :loading="impersonatingDriverId === item.id"
          title="Wejdź jako ten kierowca (otworzy aplikację kierowcy)"
          @click="impersonateDriver(item)"
        >
          <v-icon x-small left>mdi-cellphone-key</v-icon>Wejdź jako
        </v-btn>
      </template>
      <template v-slot:item.deleteDriver="{ item }">
        <button class="row-btn row-btn--error" @click="deleteDriver(item.id)" :title="$t('common.delete')">
          <v-icon size="14">mdi-delete</v-icon>
        </button>
      </template>
    </v-data-table>
    <!-- Form Dialog -->
    <v-dialog v-model="dialog" max-width="640px">
      <v-card class="driver-dialog">
        <div class="dialog-header">
          <div class="dialog-icon"><v-icon size="22" color="#fff">mdi-account-plus</v-icon></div>
          <div>
            <div class="dialog-eyebrow">{{ action || 'Tworzenie' }}</div>
            <div class="dialog-title">{{ $t('driverPage.driverForm') }}</div>
          </div>
          <button class="dialog-close" @click="closeDialog" :title="$t('common.cancel')">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <div class="dialog-body">
          <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
            <!-- Sekcja: Dane osobowe -->
            <div class="form-section-title">
              <v-icon size="16" color="#5478FF">mdi-account</v-icon>
              <span>Dane osobowe</span>
            </div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.firstName"
                  :label="$t('driverPage.labels.firstName')"
                  :rules="[rules.required]"
                  required
                  outlined dense
                  prepend-inner-icon="mdi-account"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.lastName"
                  :label="$t('driverPage.labels.lastName')"
                  :rules="[rules.required]"
                  required
                  outlined dense
                  prepend-inner-icon="mdi-account"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.phone"
                  :label="$t('driverPage.labels.phone')"
                  type="tel"
                  :rules="[rules.required]"
                  required
                  outlined dense
                  prepend-inner-icon="mdi-phone"
                />
              </v-col>
            </v-row>

            <!-- Sekcja: Logowanie -->
            <div class="form-section-title">
              <v-icon size="16" color="#5478FF">mdi-shield-key</v-icon>
              <span>Dane logowania</span>
            </div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.email"
                  :label="$t('driverPage.labels.email')"
                  :rules="[rules.required, rules.email]"
                  required
                  outlined dense
                  prepend-inner-icon="mdi-email"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.password"
                  :label="$t('driverPage.labels.password')"
                  :type="showPwd ? 'text' : 'password'"
                  :rules="[rules.required, rules.password]"
                  required
                  outlined dense
                  prepend-inner-icon="mdi-lock"
                  :append-icon="showPwd ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="showPwd = !showPwd"
                />
              </v-col>
            </v-row>

            <!-- Sekcja: Przypisanie -->
            <div class="form-section-title">
              <v-icon size="16" color="#5478FF">mdi-domain</v-icon>
              <span>Firma i oddział</span>
            </div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.companyId"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  :label="$t('driverPage.labels.company')"
                  :rules="[rules.required]"
                  required
                  outlined dense
                  prepend-inner-icon="mdi-domain"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.subCompanyIds"
                  :items="subCompanies"
                  multiple
                  item-text="name"
                  item-value="id"
                  :label="$t('driverPage.labels.department')"
                  :rules="[rules.required]"
                  required
                  outlined dense
                  prepend-inner-icon="mdi-office-building-outline"
                />
              </v-col>
            </v-row>
          </v-form>
        </div>

        <div class="dialog-footer">
          <button class="action-pill action-pill--neutral" @click="closeDialog">
            {{ $t('common.cancel') }}
          </button>
          <button class="action-pill action-pill--primary" @click="submitForm" :disabled="!valid">
            <v-icon size="14">mdi-content-save</v-icon>{{ $t('common.save') }}
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const axios = require("axios");
import Swal from "sweetalert2";
import BoltQualityPanel from "@/components/bolt/BoltQualityPanel.vue";
export default {
  components: { BoltQualityPanel },
  data() {
    return {
      impersonatingDriverId: null,
      search: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      totalItems: 0,
      drivers: [],
      headers: [
        { text: "", value: "details", sortable: false, width: 60 },
        { text: "Gotowy do rozliczeń?", align: "start", value: "isReadyForSettlements", sortable: true },
        { text: "Wewnętrzny Id", align: "start", value: "numericId", sortable: true },
        { text: "Imię", align: "start", value: "firstName", sortable: true },
        { text: "Nazwisko", align: "start", value: "lastName", sortable: true },
        { text: "Student?", align: "start", value: "isStudent", sortable: true },
        { text: "Mail", align: "start", value: "email", sortable: true },
        { text: "Telefon", align: "start", value: "phone", sortable: true },
        { text: "Częstotliwość Wypłat", align: "start", value: "payoutSchedule", sortable: true },
        { text: "Rodzaj Współpracy", align: "start", value: "cooperationType", sortable: true },
        { text: "Komornik?", align: "start", value: "repossesion", sortable: true },
        { text: "Auto", align: "start", value: "car", sortable: true },
        { text: "Powiązane Firmy/Oddziały", align: "start", value: "subcompanyNames", sortable: true },
        { text: "", value: "deleteDriver", sortable: false, width: 60 },
      ],
      loading: false,
      dialog: false,
      valid: false,
      action: '',
      showPwd: false,
      selectSubCompanyId: null,
      formData: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        phone: "",
        companyId: null,
        departmentIds: [],
        subCompanyIds: [],
      },
      companies: [],
      departments: [],
      subCompanies: [],
      rules: {
        required: (value) => !!value || "Wymagane.",
        email: (value) => /.+@.+\..+/.test(value) || "Nieprawidłowy email.",
        password: (value) =>
          value.length >= 6 || "Hasło musi mieć co najmniej 6 znaków.",
      },
    };
  },
  watch: {
    "formData.subCompanyIds": function (newVal) {
      if (newVal) {
        this.getDepartmentsSelect();
      } else {
        this.departments = [];
      }
    },
    search: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
    selectSubCompanyId: {
      handler() {
        this.initialize();
      },
    },
  },
  async created() {
          await this.getCompanySelect();

          await this.getSubcompanySelect();

    await this.initialize();
  },
  computed: {
    isHeadAdmin() {
      return localStorage.getItem('actualRole') === 'headadmin'
        || localStorage.getItem('isHead') === 'true';
    },
  },
  methods: {
    async impersonateDriver(item) {
      this.impersonatingDriverId = item.id;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'admin-auth/impersonate/driver/' + item.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const token = res.data && res.data.access_token;
        const base = process.env.VUE_APP_DRIVER_URL;
        if (!base) {
          Swal.fire('Brak konfiguracji', 'Ustaw VUE_APP_DRIVER_URL (adres aplikacji kierowcy), aby otwierać podgląd konta kierowcy.', 'warning');
          return;
        }
        const url = base.replace(/\/$/, '') + '/?impersonationToken=' + encodeURIComponent(token);
        window.open(url, '_blank', 'noopener');
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się wejść na konto kierowcy', 'error');
      } finally {
        this.impersonatingDriverId = null;
      }
    },
    validateUUID(value) {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return !value || uuidRegex.test(value) || "Nieprawidłowy format UUID";
    },
    validateOptionalUUID(value) {
      return value === null || this.validateUUID(value.id || value);
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    async getSubcompanySelect() {
      await this.toggleLoading(true);
      try {
        const subCompanies = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `sub-company`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.subCompanies = subCompanies.data.data.map((it) => {
          return {
            id: it.id,
            name: it.name,
          };
        });
      } catch (e) {
        console.log(e);
        const msg = e?.response?.data?.message?.toString?.() || e?.message || "Nieznany błąd sieciowy";
        Swal.fire("Błąd", msg, "error");
      }
      await this.toggleLoading(false);
    },
    async infoDriver(id) {
      location.href = `/driver-details?id=${id}`;
    },
    async deleteDriver(id) {
      Swal.fire({
        title: "Napewno usunąć?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "delete",
              url: process.env.VUE_APP_ROOT_API + "drivers/" + id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response.data.message.toString(), "error");
          }
        }
      });
    },
    async getDepartmentsSelect() {
      await this.toggleLoading(true);
      try {
        const departaments = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `departments`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          params: {
            subCompanyIds: this.formData.subCompanies,
          },
        });
        this.departments = departaments.data.data.map((it) => {
          return {
            id: it.id,
            name: it.name,
          };
        });
      } catch (e) {
        console.log(e);
        const msg = e?.response?.data?.message?.toString?.() || e?.message || "Nieznany błąd sieciowy";
        Swal.fire("Błąd", msg, "error");
      }
      await this.toggleLoading(false);
    },
    async submitForm() {
      // Validate the form
      if (this.$refs.form.validate()) {
        try {
          await this.toggleLoading(true);
          await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "drivers",
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
            data: {
              ...this.formData,
            },
          });
          this.closeDialog();
          await this.toggleLoading(false);
          await this.initialize();
        } catch (e) {
          await this.toggleLoading(false);
          console.log(e);
          Swal.fire("Błąd", e.response.data.message.toString(), "error");
        }
      }
    },

    async getCompanySelect() {
      await this.toggleLoading(true);
      try {
        const companies = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `company`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.companies = companies.data.data.map((it) => {
          return {
            id: it.id,
            name: it.name,
          };
        });
      } catch (e) {
        console.log(e);
        const msg = e?.response?.data?.message?.toString?.() || e?.message || "Nieznany błąd sieciowy";
        Swal.fire("Błąd", msg, "error");
      }
      await this.toggleLoading(false);
    },
    closeDialog() {
      this.dialog = false;
      this.currentDriverId = undefined;
      this.$refs.form.resetValidation(); // Reset form validation
    },
    async initialize() {
      this.loading = true;
      try {
        const drivers = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `drivers`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          params: {
            take: 10000,
            skip: 1,
            search: this.search,
            subCompanyIds: this.selectSubCompanyId?.length > 0
              ? this.selectSubCompanyId.map((it) => it.id)
              : [],
          },
        });
        this.drivers = drivers.data.data.result.map((p) => {
          return {
            id: p.id,
            numericId: p.numericId,
            email: p.email,
            firstName: p.firstName,
            lastName: p.lastName,
            phone: p.phone,
            isReadyForSettlements: p.isReadyForSettlements ? '🟢' : '🔴',
            isStudent: p.zusType === "STUDENT" ? "Tak" : "Nie",
            repossesion: p.isReposession ? "Tak" : "Nie",
            car: p.car?.plate || p.secondCar?.plate || "",
            payoutSchedule: p.payoutSchedule,
            cooperationType: p.cooperationType,
            companyName: p.company.name,
            subcompanyNames: p.subCompanies
              .map((it) => it.subcompany.name)
              .toString(),
          };
        });
        this.totalItems = drivers.data.data.count;
      } catch (e) {
        console.log(e);
        const msg = e?.response?.data?.message?.toString?.() || e?.message || "Nieznany błąd sieciowy";
        Swal.fire("Błąd", msg, "error");
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
/* === Driver-app aesthetic for admin Drivers === */
.drivers-page {
  background: #181C28;
  min-height: calc(100vh - 56px);
  padding: 16px 16px 40px;
  margin: -24px;
  color: #fff;
}
.drivers-page * { box-sizing: border-box; }

/* Page header */
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 4px 18px;
}
.page-eyebrow {
  font-size: 12px;
  color: #6E7691;
  font-weight: 500;
  text-transform: capitalize;
}
.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  margin: 2px 0 0;
  letter-spacing: -0.02em;
}
.page-header-actions { display: flex; gap: 8px; flex-wrap: wrap; }

/* Filter section */
.filter-section {
  background: #232838;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 18px;
  padding: 16px 18px;
}
.filter-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(41, 98, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.filter-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
  flex-grow: 1;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

/* Action pills */
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #181C28;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: transform 0.12s ease, background 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
}
.action-pill:hover { transform: translateY(-1px); border-color: rgba(255,255,255,0.18); }
.action-pill:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.action-pill .v-icon { color: inherit !important; }
.action-pill--primary { background: #2962FF; border-color: #2962FF; box-shadow: 0 4px 12px rgba(41,98,255,0.3); }
.action-pill--info    { background: rgba(66, 165, 245, 0.18); border-color: rgba(66,165,245,0.35); color: #64B5F6; }
.action-pill--success { background: rgba(45, 203, 115, 0.18); border-color: rgba(45,203,115,0.35); color: #6FE89A; }
.action-pill--warning { background: rgba(255, 152, 0, 0.18); border-color: rgba(255,152,0,0.35); color: #FFB74D; }
.action-pill--error   { background: rgba(244, 67, 54, 0.18); border-color: rgba(244,67,54,0.35); color: #FF8A80; }
.action-pill--neutral { background: rgba(255, 255, 255, 0.06); border-color: rgba(255,255,255,0.12); color: #B0BEC5; }

/* Row buttons (smaller, icon-only) */
.row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform 0.1s ease;
  font-family: inherit;
  background: rgba(255,255,255,0.06);
  color: #B0BEC5;
}
.row-btn:hover { transform: translateY(-1px); }
.row-btn--warning { background: rgba(255, 152, 0, 0.18); color: #FFB74D; }
.row-btn--error   { background: rgba(244, 67, 54, 0.18); color: #FF8A80; }
.row-btn .v-icon { color: inherit !important; }

/* Search input */
.search-input {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #181C28;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 10px 14px;
}
.search-input input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
}
.search-input input::placeholder { color: #6E7691; }

/* === Vuetify deep overrides === */
.drivers-page >>> .v-card {
  background: #232838 !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 18px !important;
  box-shadow: none !important;
  color: #fff !important;
}
.drivers-page >>> .v-card .v-card__title {
  color: #fff !important;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  padding: 16px 20px 8px;
}

/* Inputs */
.drivers-page >>> .v-text-field--outlined fieldset,
.drivers-page >>> .v-select--outlined fieldset {
  border-color: rgba(255,255,255,0.1) !important;
}
.drivers-page >>> .v-text-field--outlined .v-input__slot,
.drivers-page >>> .v-select--outlined .v-input__slot {
  background: #181C28 !important;
  border-radius: 12px !important;
  min-height: 44px !important;
}
.drivers-page >>> .v-label { color: #6E7691 !important; font-size: 13px !important; }
.drivers-page >>> .v-text-field input,
.drivers-page >>> .v-select__selection { color: #fff !important; }
.drivers-page >>> .v-input__prepend-inner .v-icon { color: #6E7691 !important; }

/* Plain text fields inside dialog (not outlined) */
.drivers-page >>> .v-dialog .v-text-field input,
.drivers-page >>> .v-dialog .v-select__selection { color: #fff !important; }
.drivers-page >>> .v-dialog .v-text-field .v-label,
.drivers-page >>> .v-dialog .v-select .v-label { color: #6E7691 !important; }

/* Data table */
.drivers-page >>> .v-data-table {
  background: #232838 !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 18px !important;
  color: #fff !important;
  box-shadow: none !important;
}
.drivers-page >>> .v-data-table thead tr th {
  background: #1F2434 !important;
  color: #6E7691 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid rgba(255,255,255,0.06) !important;
}
.drivers-page >>> .v-data-table tbody tr { background: #232838 !important; }
.drivers-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.drivers-page >>> .v-data-table tbody tr td {
  color: #fff !important;
  border-bottom: 1px solid rgba(255,255,255,0.04) !important;
}
.drivers-page >>> .v-data-footer {
  background: #1F2434 !important;
  color: #B0BEC5 !important;
  border-top: 1px solid rgba(255,255,255,0.06) !important;
}

/* Chips, dividers, progress */
.drivers-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.drivers-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.drivers-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }

/* Dialog */
.drivers-page >>> .v-dialog .v-card { background: #232838 !important; }

/* === Driver-create dialog === */
.driver-dialog {
  background: #232838 !important;
  border-radius: 22px !important;
  overflow: hidden;
}
.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  background: linear-gradient(135deg, #1F2434 0%, #232838 100%);
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.dialog-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 6px 18px rgba(41, 98, 255, 0.4);
}
.dialog-eyebrow {
  font-size: 11px;
  color: #6E7691;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.dialog-title {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.02em;
  margin-top: 1px;
}
.dialog-close {
  margin-left: auto;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #B0BEC5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}
.dialog-close:hover { background: rgba(244, 67, 54, 0.18); color: #FF8A80; }
.dialog-close .v-icon { color: inherit !important; }
.dialog-body {
  padding: 16px 20px 8px;
  max-height: calc(85vh - 180px);
  overflow-y: auto;
}
.form-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 14px 0 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6E7691;
}
.form-section-title:first-child { margin-top: 4px; }
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px 18px;
  border-top: 1px solid rgba(255,255,255,0.06);
  background: #1F2434;
}

/* Scrollbar for dialog-body */
.dialog-body::-webkit-scrollbar { width: 6px; }
.dialog-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
}
.dialog-body::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.16); }

/* Force outlined input style inside dialog (already covered by .drivers-page deep override but
   v-dialog renders into a portal — needs its own scope) */
.driver-dialog >>> .v-text-field--outlined fieldset,
.driver-dialog >>> .v-select--outlined fieldset {
  border-color: rgba(255,255,255,0.1) !important;
}
.driver-dialog >>> .v-text-field--outlined .v-input__slot,
.driver-dialog >>> .v-select--outlined .v-input__slot {
  background: #181C28 !important;
  border-radius: 12px !important;
  min-height: 44px !important;
}
.driver-dialog >>> .v-label { color: #6E7691 !important; font-size: 13px !important; }
.driver-dialog >>> .v-text-field input,
.driver-dialog >>> .v-select__selection { color: #fff !important; }
.driver-dialog >>> .v-input__prepend-inner .v-icon,
.driver-dialog >>> .v-input__append-inner .v-icon { color: #6E7691 !important; }
.driver-dialog >>> .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #5478FF !important;
}
.driver-dialog >>> .v-messages__message {
  color: #FF8A80 !important;
  font-size: 11px;
}
</style>
